import React, { useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { isFormIncomplete } from '../../../helpers/formState';
import { Form, Input, Row, Col, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { getValidAcctEmail, handleAlphaKeyPress, isUndefinedNullOrEmpty } from '../../../common/common';
import { getAccountCheckModel, getCreateAccountInfoModel } from '../../../helpers/OrderHelperFunctions';
import { useDispatch } from 'react-redux';
import { useAccountCheck, useLoading, useCartData, useMbox, useSessionData } from '../../../hooks/order';
import { accountCheck, saveChgQuestionInfo, savePassword, updateErrorMsg, updateOrder } from '../../../store/action/OrderAction';
import { SecQueOptions } from '../../../util/util';
import { emailIdRegex, lowerCaseRegex, numberRegex, specialCharRegex, uppercaseRegex } from '../../../util/regex';
import { Loader } from '../../common/Loader';
import { getConfigs } from '../../../config';
import useLeadsSDK from '../../../hooks/useLeadsSDK';
import useRevalidateOnLanguageChange from '../../../hooks/useRevalidateOnLanguageChange';
const config = getConfigs();

const CreateAccount = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const leads = useLeadsSDK();
  const [form] = Form.useForm();
  useRevalidateOnLanguageChange(form);
  const emailInp = useRef();
  const dispatch = useDispatch();
  const { loading } = useLoading();
  const { checkAccount, checkingAccount } = useAccountCheck();
  const { orderInfo } = useSessionData();
  const { isElnkMailboxReq } = useMbox();
  const { orderAccessToken, firstName, lastName, email } = orderInfo;
  const [emailInvalidMsg, setEmailInvalidMsg] = useState('');
  const [emailIsAvailable, setEmailIsAvailable] = useState('');
  const [suggestedIds, setSuggestedIds] = useState([]);
  const { vasPlan } = useCartData();
  const { Option } = Select;
  const [btnText, setBtnText] = useState('Check');

  const showELNKAccountCreationFields = useMemo(() => {
    return config.SHOW_PSWD_CQAS === 'true' || !!vasPlan.find((i) => i.servCode === 'onlnbckp');
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      accountEmail: !isUndefinedNullOrEmpty(orderInfo.accountEmail) ?
        getValidAcctEmail(orderInfo.accountEmail, isElnkMailboxReq) :
        isUndefinedNullOrEmpty(orderInfo.email) ?
          '' :
          getValidAcctEmail(orderInfo.email, isElnkMailboxReq)
    })
    checkAcctAvail();
  }, [email, isElnkMailboxReq])


  const onFinish = (values) => {
    const { accountEmail, question = '', answer = '', password } = values;
    // IF USER ID AVAILABLE THEN UPDATE ORDER
    if (!isUndefinedNullOrEmpty(orderAccessToken) && emailIsAvailable) {
      const accountInfoModel = getCreateAccountInfoModel(accountEmail, question, answer, isElnkMailboxReq)
      if (props.showCreateAccTile) {
        dispatch(savePassword(password));
        dispatch(saveChgQuestionInfo({
          question: SecQueOptions[question],
          answer: answer.trim(),
        }));
      }
      // This block will update email of userInfo and leadinfo object in case user edits the ID, if already exists
      /**
       * Created account for user.
       */
      leads?.updateOrder?.('Update Order: Set Account Email', {
        account: {
          contactEmail: email,
        },
        accountEmail,
      });
      dispatch(updateOrder(accountInfoModel, orderAccessToken)).then(() => {
        props.setActiveKey('5');
      }).catch((err) => {
        dispatch(updateErrorMsg(err))
      });
    }
  }

  const accError = form.getFieldError('accountEmail');
  const accountEmail = form.getFieldValue('accountEmail');

  const checkAcctAvail = () => {
    const accEmail = form.getFieldValue('accountEmail');
    const err = form.getFieldError('accountEmail')
    setBtnText('Check');
    if (accEmail && !isElnkMailboxReq) {
      if (accEmail && !isElnkMailboxReq && emailIdRegex.test(accEmail)) {
        form.setFields([
          {
            name: 'accountEmail',
            errors: [t('validation.invalidEmail')],
          },
        ]);
        return;
      }
    }
    if (accEmail && accEmail.length >= 3 && err?.length === 0) {
      if (!isUndefinedNullOrEmpty(accEmail) && !isUndefinedNullOrEmpty(firstName) && !isUndefinedNullOrEmpty(lastName)) {
        const accountCheckModel = getAccountCheckModel(firstName, lastName, accEmail);
        dispatch(accountCheck(accountCheckModel)).then(() => {
          if (checkAccount && checkAccount.emailIsAvailable) {
            setEmailIsAvailable(true);
            setEmailInvalidMsg('');
          } else {
            if (isElnkMailboxReq) {
              setEmailIsAvailable(false);
              setEmailInvalidMsg(t('createAccount.idUnavailable'));
              setSuggestedIds(checkAccount?.suggestedIds);
            } else {
              props.setShowCallSupportModal(true);
            }
          }
        }).catch((e) => {
          if (e.code === 'ECONNABORTED') {
            setBtnText(t('common.retry'));
          } else if (isElnkMailboxReq) {
            setEmailIsAvailable(false);
            setEmailInvalidMsg(t('createAccount.tryLater'));
            setSuggestedIds(suggestedIds);
          } else {
            props.setShowCallSupportModal(true);
          }
        })
      }
    }
  }

  const handleSuggestionClick = (data) => {
    form.setFieldsValue({
      accountEmail: data
    });
    setEmailIsAvailable(true);
  }

  useImperativeHandle(ref, () => ({
    emailEdit() {
      handleEditEmail();
    },
  }));

  const handleEditEmail = () => {
    props.setShowCallSupportModal(false);
    form.setFieldsValue({
      accountEmail: accountEmail
    });
    emailInp.current.input.focus();
    return;
  }
  return (
    <>
      <div className="create-account same-common label">
        <Form
          name="basic"
          initialValues={{ remember: true }}
          form={form}
          onFinish={onFinish}
          validateTrigger={'onBlur'}
        >
          <Row className="responsive-width-n">
            <Col span={19}>
              <Form.Item
                label={t('createAccount.email')}
                className="input-type-span"
                name="accountEmail"
                rules={[{ required: true, message: t('createAccount.enterEmail') }, {
                  min: 3,
                  message: t('validation.min3Length')
                },
                {
                  validator(_, value) {
                    if (!isUndefinedNullOrEmpty(value)) {
                      if (!isElnkMailboxReq && emailIdRegex.test(value)) {
                        return Promise.reject(new Error(t('validation.invalidEmail')))
                      }
                    }
                    return Promise.resolve();
                  }
                }]}
              >
                <Input onBlur={checkAcctAvail}
                  ref={emailInp}
                  onChange={() => {
                    setEmailIsAvailable(false);
                    setEmailInvalidMsg('');
                    setSuggestedIds([]);
                  }} />
              </Form.Item>
              {isElnkMailboxReq && <span className="input-span">@earthlink.net</span>}
            </Col>
            <Col span={1} className="responsive-width-set"></Col>
            {
              !checkingAccount
                ?
                <Col span={4} className="responsive-width-set">
                  <button className="Check-account blue-button"
                    onClick={checkAcctAvail}
                    disabled={(accountEmail && accountEmail.length <= 3) || emailIsAvailable || accError.length > 0}
                  >
                    {btnText === 'Check' ? t('common.check') : btnText === 'Retry' ? t('common.retry') : btnText}
                  </button>
                </Col>
                :
                <div className={'accountLoading check-loading-reponsive text-center'}>
                  <Loader />
                </div>
            }
          </Row>
          {
            !emailIsAvailable && emailInvalidMsg !== '' &&
            <p className="red-color email-suggest-msg">{emailInvalidMsg}</p>
          }
          {
            !emailIsAvailable && suggestedIds.length > 0 ?
              <div className="suggestedIds">
                <p className="red-color id-suggest-msg">{t('createAccount.suggestedIds')}</p>
                <ul className="suggestionsContainer">
                  {
                    suggestedIds.map((suggestId, i) => {
                      return (
                        <li key={i} className="suggestions" onClick={() => handleSuggestionClick(suggestId)}>{suggestId}</li>
                      )
                    })
                  }
                </ul>
              </div>
              : ''
          }
          {showELNKAccountCreationFields && <>
            <Row>
              <Col span={24} className="responsive-width-set">
                <Form.Item
                  label={t('createAccount.password')}
                  name="password"
                  rules={[{ required: true, message: t('createAccount.enterPassword') }, {
                    validator(_, value) {
                      if (!isUndefinedNullOrEmpty(value)) {
                        let validateCondition = 0
                        if (value.match(specialCharRegex) && value.match(specialCharRegex).length > 0) {
                          validateCondition += 1;
                        }
                        if (value.match(uppercaseRegex) && value.match(uppercaseRegex).length > 0) {
                          validateCondition += 1;
                        }
                        if (value.match(lowerCaseRegex) && value.match(lowerCaseRegex).length > 0) {
                          validateCondition += 1;
                        }
                        if (value.match(numberRegex) && value.match(numberRegex).length > 0) {
                          validateCondition += 1;
                        }
                        if (validateCondition < 3 || value.length < 8 || value.length > 32) {
                          return Promise.reject(new Error(t('validation.passwordInvalid')))
                        }
                        if (value.toLowerCase().includes(firstName.toLowerCase()) || value.toLowerCase().includes(lastName.toLowerCase())) {
                          return Promise.reject(new Error(t('createAccount.passwordNoName')))
                        }
                      }
                      return Promise.resolve();
                    }
                  }]}
                >
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Form.Item
                  label={t('createAccount.repeatPassword')}
                  dependencies={['password']}
                  name="RepeatPassword"
                  rules={[{ required: true, message: t('createAccount.enterRepeatPassword') },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('createAccount.passwordsNoMatch')));
                      },
                    }),]}
                >
                  <Input.Password />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={12} className="your-info-input responsive-width-set">
                <Form.Item
                  label={t('createAccount.selectQuestion')}
                  name="question"
                  rules={[{ required: true, message: t('createAccount.selectSecurityQuestion') }]}
                >
                  <Select
                    placeholder={t('createAccount.selectQuestion')}
                    dropdownMatchSelectWidth={false}
                  >
                    {
                      SecQueOptions.map((option, i) => {
                        return <Option key={i} value={i}>{option}</Option>
                      })
                    }
                  </Select>
                </Form.Item>
              </Col>
              <Col span={1}></Col>
              <Col span={11} className="your-info-input responsive-width-set">
                <Form.Item
                  label={t('createAccount.answer')}
                  name="answer"
                  validateFirst={true}
                  rules={[{
                    required: true,
                    whitespace: true,
                    message: t('createAccount.enterAnswer')
                  },
                  {
                    min: 2,
                    message: t('validation.min2Length')
                  },
                  ]}
                >
                  <Input onKeyPress={handleAlphaKeyPress} />
                </Form.Item>
              </Col>
            </Row>
          </>}
          <Row>
            <Col span={24} className="your-info-tab">
              <div className="text-right enter-your-info">
                {loading ?
                  <Loader />
                  :
                  <Form.Item shouldUpdate noStyle>
                    {() => (
                      <button
                        className="blue-button"
                        htmlType="submit"
                        disabled={props.disableWhenIncomplete && isFormIncomplete(
                          form,
                          showELNKAccountCreationFields ? ['accountEmail', 'password', 'RepeatPassword', 'question', 'answer'] : ['accountEmail']
                        )}
                      >
                        {t('common.continue')}
                      </button>
                    )}
                  </Form.Item>
                }
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  )
});
export default CreateAccount;
